/**
 * WebSocket service handler — raw binary WebSocket (no SockJS/STOMP).
 *
 * Connects to `REACT_APP_WS_URL` (e.g. `wss://stream.eunosoft.com/ws/data`).
 * The browser WebSocket API does not allow custom headers, so authentication
 * is passed via query params: `?access_token=...&client_id=...`.
 *
 * ### Tick data format (server → client)
 * Each binary frame is a UTF-8 JSON payload decoded to a 2-D array:
 * ```
 * [ [scripId, timestamp, ltq, volume, oi, ltp], ... ]
 * ```
 * All six elements are strings.  Arrays are filtered and dispatched **as-is**
 * (no object conversion) to keep the hot path allocation-free.
 *
 * Field index constants:
 *   TICK_SCRIP_ID = 0, TICK_TS = 1, TICK_LTQ = 2,
 *   TICK_VOLUME = 3,   TICK_OI = 4, TICK_LTP = 5
 *
 * ### Throttled dispatching
 * Incoming ticks are merged into a `pendingPayload` batch keyed by scripId
 * (last-writer-wins) and flushed as a single `"UPDATE_SCRIPS"` Redux action
 * every 100 ms to avoid flooding the reducer.
 *
 * ### Reconnect strategy
 * • Heartbeat check every 16 s — if no data for > 15 s, reconnect.
 * • Unexpected `onclose` triggers a 3 s delayed reconnect.
 *
 * @see {@link ../middleware/websocket-middleware.ts} for the saga that
 * processes the dispatched actions.
 */

import { getAccessToken } from "./local-storage-handler";
import { store } from "@store/index";
import { setConnectionStatus, setLastDataAt } from "@store/connection-status-slice";
import { platform } from "@platform";

// ── Tick field index constants ────────────────────────────────────────────
export const TICK_SCRIP_ID = 0;
export const TICK_TS       = 1;
export const TICK_LTQ      = 2;
export const TICK_VOLUME   = 3;
export const TICK_OI       = 4;
export const TICK_LTP      = 5;

// ── Connection state ──────────────────────────────────────────────────────
let ws: WebSocket | null = null;
let isConnecting = false;
let isConnected = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let lastDataArrivedTime = 0;

const HEARTBEAT_INTERVAL_MS = 16_000;
const STALE_DATA_MS         = 15_000;
const RECONNECT_DELAY_MS    = 3_000;
const WS_DISPATCH_INTERVAL  = 100; // ms
const LAST_DATA_STATUS_UPDATE_INTERVAL_MS = 1_000;

// Reuse a single TextDecoder — no per-frame allocation.
const decoder = new TextDecoder("utf-8");

// ── Heartbeat / reconnect helpers ─────────────────────────────────────────

function scheduleReconnect() {
  if (reconnectTimer) return;
  store.dispatch(setConnectionStatus("reconnecting"));
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connectWS();
  }, RECONNECT_DELAY_MS);
}

function startHeartbeat() {
  stopHeartbeat();
  heartbeatInterval = setInterval(() => {
    if (Date.now() - lastDataArrivedTime > STALE_DATA_MS) {
      stopHeartbeat();
      store.dispatch(setConnectionStatus("reconnecting"));
      closeSocket();
      connectWS();
    }
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

/** Close the raw socket without triggering the reconnect handler. */
function closeSocket() {
  if (ws) {
    ws.onclose = null; // prevent scheduleReconnect from firing
    ws.onerror = null;
    ws.close();
    ws = null;
  }
  isConnected = false;
}

// ── Throttled dispatch ────────────────────────────────────────────────────

// payloadBody is string[][] — each row: [scripId, ts, ltq, vol, oi, ltp]
let pendingPayload: string[][] | null = null;
let dispatchTimer: ReturnType<typeof setTimeout> | null = null;
let pendingByScripId: Map<number, string[]> = new Map();
let lastStatusUpdateTime = 0;
let cachedScripIdsRef: number[] | null = null;
let cachedScripIdSet: Set<number> = new Set();

const flushPendingData = () => {
  dispatchTimer = null;
  if (pendingByScripId.size > 0) {
    const data = Array.from(pendingByScripId.values());
    pendingByScripId = new Map();
    pendingPayload = null;
    store.dispatch({
      type: "UPDATE_SCRIPS",
      payload: { payloadBody: data },
    });
  }
};

// ── onmessage ─────────────────────────────────────────────────────────────

const onData = (event: MessageEvent) => {
  lastDataArrivedTime = Date.now();
  if (lastDataArrivedTime - lastStatusUpdateTime >= LAST_DATA_STATUS_UPDATE_INTERVAL_MS) {
    lastStatusUpdateTime = lastDataArrivedTime;
    store.dispatch(setLastDataAt(new Date(lastDataArrivedTime).toISOString()));
  }

  const scripIds = store.getState().watchlist.scripIds;
  if (scripIds.length === 0) return;
  if (cachedScripIdsRef !== scripIds) {
    cachedScripIdsRef = scripIds;
    cachedScripIdSet = new Set(scripIds);
  }

  // Decode binary frame (ArrayBuffer) → UTF-8 text → array-of-arrays.
  let ticks: string[][];
  try {
    const text =
      event.data instanceof ArrayBuffer
        ? decoder.decode(event.data)
        : (event.data as string); // text-frame fallback
    ticks = JSON.parse(text);
    if (!Array.isArray(ticks)) return;
  } catch {
    return; // malformed frame — skip silently
  }

  // Filter to subscribed scrips (index 0 = scripId string).
  // Use a for-loop instead of Array.filter to avoid allocating an intermediate
  // array when the match rate is low.
  const filtered: string[][] = [];
  for (let i = 0; i < ticks.length; i++) {
    const tick = ticks[i];
    if (tick && cachedScripIdSet.has(parseInt(tick[TICK_SCRIP_ID], 10))) {
      filtered.push(tick);
    }
  }
  if (filtered.length === 0) return;

  // Merge into pending batch (last-writer-wins per scripId).
  for (const t of filtered) {
    pendingByScripId.set(parseInt(t[TICK_SCRIP_ID], 10), t);
  }
  pendingPayload = Array.from(pendingByScripId.values());

  if (!dispatchTimer) {
    dispatchTimer = setTimeout(flushPendingData, WS_DISPATCH_INTERVAL);
  }
};

// ── Public API ────────────────────────────────────────────────────────────

export const connectWS = () => {
  return new Promise<void>((resolve) => {
    if (isConnecting || isConnected) {
      resolve();
      return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      // No token yet (OAuth flow not complete) — skip silently.
      // connectWS() will be retried once the token is stored and the
      // TitleBar's isOnline effect re-fires (e.g. after a heartbeat cycle).
      resolve();
      return;
    }

    isConnecting = true;
    store.dispatch(setConnectionStatus("connecting"));
    const clientId = platform.getOAuthClientId() || process.env.REACT_APP_CLIENTID || "";

    // Build WebSocket URL. Browser WS API does not allow custom headers,
    // so auth is passed as query params that the server reads as fallback.
    let wsBase = process.env.REACT_APP_WS_URL ?? "";
    wsBase = wsBase.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
    const query = new URLSearchParams();
    query.set("access_token", accessToken);
    if (clientId) {
      query.set("client_id", clientId);
    }
    const wsUrl = `${wsBase}?${query.toString()}`;

    const socket = new WebSocket(wsUrl);
    // Receive binary frames as ArrayBuffer for synchronous TextDecoder decode.
    socket.binaryType = "arraybuffer";
    ws = socket;

    socket.onopen = () => {
      isConnecting = false;
      isConnected  = true;
      lastDataArrivedTime = Date.now();
      store.dispatch(setConnectionStatus("connected"));
      startHeartbeat();
      resolve();
    };

    socket.onmessage = onData;

    socket.onclose = (event) => {
      isConnected  = false;
      isConnecting = false;
      ws = null;
      stopHeartbeat();
      store.dispatch(setConnectionStatus("disconnected"));
      if (!event.wasClean) {
        // Server closed unexpectedly or network dropped — reconnect.
        scheduleReconnect();
      }
    };

    socket.onerror = () => {
      isConnecting = false;
      // onclose fires immediately after onerror; let it handle state + reconnect.
      resolve(); // resolve even on error (mirrors original behaviour)
    };
  });
};

export const disconnectWS = () => {
  if (!isConnected && !isConnecting) return;

  // Cancel pending dispatch to prevent stale Redux updates.
  if (dispatchTimer) {
    clearTimeout(dispatchTimer);
    dispatchTimer = null;
  }
  pendingPayload = null;
  pendingByScripId = new Map();

  // Cancel any scheduled reconnect — this is a deliberate disconnect.
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  stopHeartbeat();
  closeSocket();
  store.dispatch(setConnectionStatus("disconnected"));
};
